import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Session, sessionHelper } from "@newLayouts/auth/libs/common";
import { HttpRequest } from "@maven-rest/common/HttpRequest";

interface SessionContextProps {
  session: Session | null;
  onSetSession: (session: Session) => void;
  onDeleteSession: () => void;
}

export const SessionContext = createContext<SessionContextProps | null>(null);

const useSessionContext = (): SessionContextProps => {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSessionContext must be used within SessionContextProvider");
  }

  return context;
};

function SessionContextProvider({ children }: { children: React.ReactNode }) {
  const initialSession = sessionHelper.getSession();
  const [session, setSession] = useState<Session | null>(initialSession);

  const handleSetSession = useCallback((session: Session) => {
    sessionHelper.setSession(session);
    setSession(session);
  }, []);

  const handleDeleteSession = useCallback(() => {
    sessionHelper.clearSession();
    setSession(null);
  }, []);

  if (session?.token) {
    HttpRequest.setDefaultAuthorization(session.token);
  } else {
    HttpRequest.clearDefaultAuthorization();
  }

  const value = useMemo(
    () => ({
      session,
      onSetSession: handleSetSession,
      onDeleteSession: handleDeleteSession,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSetSession, handleDeleteSession, session?.expiryAt, session?.token]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export { SessionContextProvider, useSessionContext };
